import { formatCurrency } from "@angular/common";
import { AfterViewInit, ChangeDetectorRef, Component, Input, LOCALE_ID, OnChanges, OnDestroy, OnInit, Signal, SimpleChanges, ViewChild, inject } from "@angular/core";
import { Subject, takeUntil } from "rxjs";
import { ChartJsContainerComponent } from "@tylertech/chart-js";
import { ChartService } from "@tylertech/charting-module";
import { NavigationService, ThemeService, UrlService } from "@tylertech/core-module";
import { Utilities } from "@tylertech/shared-module";
import { AccountDetailViewModel, AppLinkParms, ExecutionResult, FilterItem } from "@tylertech/tyler-api-types";
import { ActiveElement, Chart, ChartConfiguration, ChartEvent, TooltipItem, TooltipModel } from "chart.js";

@Component({
    selector: "account-detail-pie-chart",
    templateUrl: "./accountDetailPieChart.tmpl.html",
    styleUrls: [
        "./accountDetailPieChart.component.scss"
    ],
    imports: [ChartJsContainerComponent]
})
export class AccountDetailPieChartComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy
{
    private readonly $navigationService = inject(NavigationService);
    private readonly $urlService = inject(UrlService);
    private readonly $chartService = inject(ChartService);
    private readonly locale = inject(LOCALE_ID);
    private readonly cdr = inject(ChangeDetectorRef);

    private labels: Array<string> = ["Budget Available", "Budget Used"];
    private pendingChartUpdate = false;
    private readonly destroy$ = new Subject<void>();
    @ViewChild(ChartJsContainerComponent) private readonly chartContainer: ChartJsContainerComponent;
    chartConfig: ChartConfiguration<"pie">;

    @Input() account: AccountDetailViewModel;
    @Input() appName: string;

    readonly isDarkTheme: Signal<boolean>;

    constructor()
    {
        const $themeService = inject(ThemeService);

        this.isDarkTheme = $themeService.isDarkTheme;
    }

    get hasData(): boolean
    {
        const datasets = this.chartConfig?.data.datasets;
        if (!datasets)
        {
            return false;
        }
        const [budgetRemaining, budgetUsed] = datasets[0].data as [number, number];
        return !isNaN(budgetRemaining) && (budgetRemaining !== budgetUsed || budgetRemaining !== 0);
    }

    ngOnInit(): void
    {
        const dataColors = this.$chartService.getColors(2);

        this.chartConfig = {
            type: "pie",
            data: {
                labels: this.labels,
                datasets: [
                    {
                        label: "",
                        data: this.getBudgetAmounts(),
                        backgroundColor: [dataColors[0], dataColors[1]],
                        borderColor: this.isDarkTheme() ? "#2c2c2c" : "#ffffff",
                    }
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: {
                        display: true,
                        position: "bottom",
                        onClick: null,
                    },
                    tooltip: {
                        callbacks: {
                            label: ((tooltipItem: TooltipItem<"pie">): string | Array<string> =>
                            {
                                const label = this.labels[tooltipItem.dataIndex];
                                let retStr = label + ": ";
                                if (label === "Budget Used")
                                {
                                    const bu = this.account.Activity + (this.account.Encumbrance || 0) + (this.account.Reserves || 0);
                                    retStr += formatCurrency(bu, this.locale, "$");
                                }
                                else
                                {
                                    let ba = this.account.BudgetRemaining;
                                    if (!Utilities.IsNullOrUndefined(this.account.Pending))
                                    {
                                        ba = ba - this.account.Pending;
                                    }
                                    retStr += formatCurrency(ba, this.locale, "$");
                                }
                                return retStr;
                            }) as unknown as (this: TooltipModel<"pie">, tooltipItem: TooltipItem<"pie">) => string | string[],
                        }
                    },
                },
                onClick: (event: ChartEvent & Event, _elements: ActiveElement[], _chart: Chart): void =>
                {
                    event?.stopPropagation();

                    if (this.appName)
                    {
                        this.$navigationService.navigateToApp(this.appName);
                    }
                    else
                    {
                        const filter: Array<FilterItem> = [
                            {
                                Key: "AccountType",
                                Value: this.account.AccountType
                            }
                        ];
                        const parms: AppLinkParms = {
                            App: "glaccountdet",
                            Parms: filter,
                        };
                        this.$urlService.GetAppLink(parms)
                            .then((executionResult: ExecutionResult<string>) =>
                            {
                                const index = executionResult.Result.indexOf("?");
                                if (executionResult.Result.includes("apps/form"))
                                {
                                    this.$navigationService.navigateToApp(executionResult.Result);
                                }
                            });
                    }
                },
            },
        };
    }

    ngAfterViewInit(): void
    {
        if (this.account && this.chartConfig)
        {
            this.updateChartData();
        }
    }

    ngOnChanges(changes: SimpleChanges): void
    {
        if (!changes.account)
        {
            return;
        }

        this.updateChartData();
    }

    private updateChartData(): void
    {
        if (!this.chartConfig)
        {
            return;
        }

        // Directly update the data in the existing config
        const newData = this.getBudgetAmounts();
        this.chartConfig.data.datasets[0].data = newData;

        // If chartContainer is available, directly access and update the Chart.js instance
        if (this.chartContainer && (this.chartContainer as any).chartObj)
        {
            const chart = (this.chartContainer as any).chartObj;
            chart.data.datasets[0].data = newData;
            chart.update('none'); // Update without animation
        }

        // Force change detection
        this.cdr.markForCheck();
    }

    ngOnDestroy(): void
    {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private getBudgetAmounts(): [number, number]
    {
        if (!this.account)
        {
            return [0, 0];
        }

        let budgetRemaining = this.account.BudgetRemaining;
        let budgetUsed = this.account.Activity + (this.account.Encumbrance || 0) + (this.account.Reserves || 0);
        if (this.account.Pending)
        {
            budgetRemaining = budgetRemaining - this.account.Pending;
        }

        if (this.account.CurrentBudget < 0)
        {
            // This is a Revenue account
            budgetRemaining = Math.abs(budgetRemaining);
            budgetUsed = Math.abs(budgetUsed);
        }
        else if (budgetRemaining < 0)
        {
            budgetRemaining = 0;
            budgetUsed = Math.abs(budgetUsed);
        }
        else
        {
            budgetUsed = Math.abs(budgetUsed);
        }

        return [budgetRemaining, budgetUsed];
    }
}
